import { LoadingCircles } from "@frontend/components/elements/LoadingCircles";
import { useIntake } from "@frontend/components/intake/contexts/useIntake";
import { getOrgById } from "@frontend/lib/actions/actions";
import { getUserById } from "@frontend/lib/queries/user/getUserById";
import { Button } from "@frontend/shared/components/ui/button";
import {
  CustomDialogContent,
  Dialog,
  DialogTrigger,
} from "@frontend/shared/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetOverlay,
  SheetPortal,
} from "@frontend/shared/components/ui/sheet";
import { cn } from "@frontend/shared/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { ArrowLeft, Check, Edit, RotateCcw, Signature, X } from "lucide-react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useParams } from "next/navigation";
import type React from "react";
import { type RefObject, useEffect, useMemo, useRef, useState } from "react";
import {
  type Control,
  type ControllerRenderProps,
  type FieldErrors,
  type UseFormGetValues,
  type UseFormSetValue,
  type UseFormTrigger,
  useWatch,
} from "react-hook-form";
import SignatureCanvas from "react-signature-canvas";
import { useMediaQuery } from "usehooks-ts";
import { formatRequiredLabel } from "./IntakeFormQuestion";

const SignatureComponent = ({
  setIsDialogueOpen,
  setSessionSignature,
  setValue,
  getValues,
  trigger,
  questionId,
  initiateAutosave,
}: {
  setIsDialogueOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSessionSignature: (signature: string) => void;
  setValue: UseFormSetValue<Partial<any>>;
  getValues: UseFormGetValues<Partial<any>>;
  trigger: UseFormTrigger<Partial<any>>;
  initiateAutosave: (data: Partial<any>, hiddenQuestions?: Set<number>) => void;
  questionId: number;
}) => {
  const userId = useParams<{ userId: string }>().userId;

  const { data: orgId } = useQuery({
    queryKey: ["getUserById", userId],
    queryFn: () => getUserById(userId),
    select: (data) => data?.orgId,
  });

  const { data } = useQuery({
    queryFn: () => getOrgById(orgId as number),
    queryKey: ["getOrgById", orgId],
    enabled: !!orgId,
  });

  const [signatureText, setSignatureText] = useState("");
  const today = format(new Date(), "MM/dd/yyyy hh:mm aa");

  const handleSignature = () => {
    setValue(`${questionId}.signature`, signatureText);
    setValue(`${questionId}.isSignatureComplete`, true);
    trigger(`${questionId}.isSignatureComplete`);

    setValue(`${questionId}.date`, today);

    setSessionSignature(signatureText);
    initiateAutosave(getValues());
    setIsDialogueOpen(false);
  };

  const t_intakeForm = useTranslations("IntakeForm");

  return data?.orgName ? (
    <div className="flex flex-col lg:w-[600px]  bg-white rounded-md">
      <div className="p-4 flex bg-[#E2E8F0] rounded-t-md">
        <p className="font-medium">{t_intakeForm("AddSignature")}</p>
      </div>
      <div className="w-full flex flex-col p-4 gap-4">
        <label className="border-[#e1e1e1] border p-4 w-full focus:border-black">
          <input
            value={signatureText}
            autoFocus={true}
            className="p-4 w-full focus:outline-none text-center italic font-light sm:text-xl lg:text-[36px] "
            placeholder={t_intakeForm("TypeNameHere")}
            onChange={(e) => {
              setSignatureText(e.target.value);
            }}
          />
        </label>

        <div className="w-full p-4 bg-[#E5E5E5] h-[90px] flex items-center justify-center">
          <p className="w-fit font-chiswick sm:text-xl lg:text-[36px]">
            {signatureText}
          </p>
        </div>
      </div>

      <p className="text-xs px-4">
        {t_intakeForm("SignatureModalBlurb", { orgName: data?.orgName })}
      </p>

      <div className="w-full flex justify-end p-4 gap-2">
        <button
          className="py-2 px-6 border border-black w-[120px] rounded-md"
          onClick={() => {
            setIsDialogueOpen(false);
          }}
        >
          <p className="text-sm">{t_intakeForm("Cancel")}</p>
        </button>
        <button
          onClick={() => {
            handleSignature();
          }}
          className="py-2 px-6 border border-black w-[120px] bg-black rounded-md"
        >
          <p className="text-sm text-white">{t_intakeForm("Sign")}</p>
        </button>
      </div>
    </div>
  ) : (
    <LoadingCircles />
  );
};

export const IntakeSignatureDialogue = ({
  field,
  register,
  currentSignature,
  setValue,
  getValues,
  trigger,
  questionId,
  updateProgress,
  isAdmin,
  initiateAutosave,
  disabled,
}: {
  field: ControllerRenderProps<Partial<any>, string>;
  register: any;
  currentSignature: string | any | null;
  setValue: UseFormSetValue<Partial<any>>;
  trigger: UseFormTrigger<Partial<any>>;
  getValues: UseFormGetValues<Partial<any>>;
  questionId: number;
  updateProgress: (updatedField: string, updatedValue: any) => void;
  isAdmin: boolean;
  initiateAutosave: (data: Partial<any>, hiddenQuestions?: Set<number>) => void;
  disabled?: boolean;
}) => {
  const [isDialogueOpen, setIsDialogueOpen] = useState(false);

  const { signature, setSignature } = useIntake();

  const today = format(new Date(), "MM/dd/yyyy hh:mm aa");
  const t_intakeForm = useTranslations("IntakeForm");

  return (
    <Dialog
      open={isDialogueOpen}
      onOpenChange={(e) => {
        if (!isAdmin && !disabled) {
          setIsDialogueOpen(e);
        }
      }}
    >
      <div className="flex w-full gap-4 h-12 rounded-sm lg:mb-0 sm:mb-8">
        <div className="w-full relative bg-[#D9D9D9] flex-1 rounded-sm ">
          <p className="absolute text-sm -top-6">
            {t_intakeForm("eSignature")}
          </p>
          {!!currentSignature && !disabled && (
            <Button
              variant="link"
              className="absolute sm:-bottom-9 lg:-bottom-7 text-sm right-0 underline cursor-pointer"
              disabled={isAdmin}
              onClick={() => {
                setValue(`${questionId}.signature`, "");
                setValue(`${questionId}.date`, "");
                setValue(`${questionId}.isSignatureComplete`, false);
                updateProgress(field.name, false);
                initiateAutosave(getValues());
              }}
            >
              {t_intakeForm("Clear")}
            </Button>
          )}

          {signature && !currentSignature ? (
            <Button
              variant="link"
              onClick={() => {
                setValue(`${questionId}.signature`, signature);
                setValue(`${questionId}.isSignatureComplete`, true);
                updateProgress(field.name, true);
                setValue(`${questionId}.date`, today);
              }}
              disabled={disabled}
              className="w-full flex bg-[#D9D9D9] items-center justify-center h-full gap-2 rounded-md cursor-pointer"
            >
              <p className="font-normal text-xs">
                {t_intakeForm("PreviousSignature")}
              </p>
            </Button>
          ) : (
            <DialogTrigger asChild={true}>
              <div
                className={cn(
                  "w-full flex bg-[#D9D9D9] items-center justify-center h-full gap-2 rounded-md",
                  isAdmin || disabled ? "cursor-default" : "cursor-pointer",
                )}
              >
                {currentSignature ? (
                  currentSignature.dataUrl || currentSignature.style ? (
                    <>
                      {currentSignature.dataUrl && (
                        <Image
                          src={currentSignature.dataUrl}
                          alt="signature"
                          width={100}
                          height={100}
                        />
                      )}
                      {currentSignature.style && (
                        <p className="font-chiswick">{currentSignature.text}</p>
                      )}
                    </>
                  ) : (
                    <div className="w-full flex p-4 relative items-center justify-start">
                      <p className="font-chiswick">{currentSignature}</p>
                      {!disabled && <Edit className="absolute right-4" />}
                    </div>
                  )
                ) : (
                  <>
                    <p className="font-normal text-xs">
                      {isAdmin ? "Not Signed" : t_intakeForm("Signature")}
                    </p>
                    {!isAdmin && <Signature size={18} strokeWidth={1} />}
                  </>
                )}
              </div>
            </DialogTrigger>
          )}

          <CustomDialogContent
            className="w-full flex items-center justify-center fixed top-[56px] h-[calc(100%-56px)] bg-[#B1B1B199]"
            style={{ backdropFilter: "blur(1.5px)" }}
          >
            <SignatureComponent
              setIsDialogueOpen={setIsDialogueOpen}
              setSessionSignature={setSignature}
              setValue={setValue}
              getValues={getValues}
              trigger={trigger}
              questionId={questionId}
              initiateAutosave={initiateAutosave}
            />
          </CustomDialogContent>
        </div>
      </div>
    </Dialog>
  );
};

export const IntakeSignatureInput = ({
  isRequired,
  questionId,
  questionText,
  questionAttributes,
  field,
  register,
  errors,
  setValue,
  getValues,
  trigger,
  control,
  updateProgress,
  isAdmin,
  initiateAutosave,
  disabled,
}: {
  isRequired: boolean;
  questionId: number;
  questionText: string;
  questionAttributes: any;
  field: ControllerRenderProps<Partial<any>, string>;
  register: any;
  errors: FieldErrors<Partial<any>>;
  setValue: UseFormSetValue<Partial<any>>;
  getValues: UseFormGetValues<Partial<any>>;
  trigger: UseFormTrigger<Partial<any>>;
  control: Control<Partial<any>, any>;
  updateProgress: (updatedField: string, updatedValue: any) => void;
  isAdmin: boolean;
  initiateAutosave: (data: Partial<any>, hiddenQuestions?: Set<number>) => void;
  disabled?: boolean;
}) => {
  const signatureField = useWatch({ control, name: field.name });

  const sanitizeHtml = (html: string) => {
    if (html.includes("<script>")) {
      throw new Error("XSS attack detected");
    }

    return html;
  };

  const t_intakeForm = useTranslations("IntakeForm");
  const t = useTranslations("IntakeFormStates");

  return (
    <div className="flex flex-col w-full">
      <div className="flex flex-col w-full gap-1 bg-[#F1F1F1] py-4 px-6 relative mb-10">
        <div className="absolute w-[2px] h-full left-0 bg-[#00CCFF] top-0" />
        <p
          className="text-sm font-medium text-[#000000]"
          data-questionid={questionId}
          data-pdflabel="signatureInput-label"
        >
          {isRequired ? `${questionText}*` : questionText}
        </p>
        <div
          data-questionid={questionId}
          data-pdflabel="signatureInput-blurb"
          // biome-ignore lint/security/noDangerouslySetInnerHtml: we control the html and sanitize it
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(questionAttributes.blurb),
          }}
        />
      </div>

      <div className="flex w-full flex-col gap-8">
        <div className="flex sm:flex-col print:flex-row print:gap-4 lg:gap-4 sm:gap-8 lg:w-full">
          <div className="flex h-fit relative print:basis-3/5 lg:basis-3/5 sm:max-w-full lg:w-full">
            <IntakeSignatureDialogue
              field={field}
              register={register}
              currentSignature={signatureField?.signature}
              setValue={setValue}
              getValues={getValues}
              trigger={trigger}
              questionId={questionId}
              updateProgress={updateProgress}
              isAdmin={isAdmin}
              initiateAutosave={initiateAutosave}
              disabled={disabled}
            />
            <p className="text-[#a92523] absolute font-light text-[10px] bottom-[-21px]">
              {(errors[questionId.toString()] as any)?.isSignatureComplete &&
                t(
                  (errors[questionId.toString()] as any)?.isSignatureComplete
                    ?.message as string,
                )}
            </p>
          </div>

          <div className="relative h-fit flex print:basis-2/5 lg:basis-2/5">
            <p className="absolute top-[-24px] text-sm">
              {t_intakeForm("Date")}
            </p>
            <input
              {...register(`${field.name}.date`)}
              readOnly={true}
              value={signatureField?.date ?? ""}
              type="text"
              className="bg-[#dadada] outline-none w-full min-h-12 flex flex-col gap-2 border px-4 pb-0 text-sm rounded-sm text-[#787878] font-chiswick"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export const TypeformIntakeSignatureInput = ({
  isRequired,
  questionId,
  questionText,
  questionAttributes,
  field,
  register,
  errors,
  setValue,
  getValues,
  trigger,
  control,
  updateProgress,
  isAdmin,
  initiateAutosave,
  allSignatureQuestions,
  disabled,
}: {
  isRequired: boolean;
  questionId: number;
  questionText: string;
  questionAttributes: any;
  field: ControllerRenderProps<Partial<any>, string>;
  register: any;
  errors: FieldErrors<Partial<any>>;
  setValue: UseFormSetValue<Partial<any>>;
  getValues: UseFormGetValues<Partial<any>>;
  trigger: UseFormTrigger<Partial<any>>;
  control: Control<Partial<any>, any>;
  updateProgress: (updatedField: string, updatedValue: any) => void;
  isAdmin: boolean;
  initiateAutosave: (data: Partial<any>, hiddenQuestions?: Set<number>) => void;
  allSignatureQuestions: any[];
  disabled?: boolean;
}) => {
  const signatureField = useWatch({ control, name: field.name });

  const [signed, setSigned] = useState(field.value?.isSignatureComplete);

  const buttonClassName =
    "rounded-full text-center font-medium text-sm border border-black bg-white text-black w-full p-6 flex items-center justify-center gap-2";

  const sanitizeHtml = (html: string) => {
    if (html.includes("<script>")) {
      throw new Error("XSS attack detected");
    }

    return html;
  };

  const t = useTranslations("IntakeFormStates");
  const t_signature = useTranslations("TypeformIntake");

  const [signingSheetOpen, setSigningSheetOpen] = useState(false);

  const sigCanvas = useRef<SignatureCanvas>(null);

  const fontClassName = useMemo(() => {
    switch (signatureField?.signature?.style) {
      case "print":
        return "font-vujahday text-2xl";
      case "classic":
        return "font-madi text-3xl";
      case "fancy":
        return "font-delafield text-3xl";
      default:
        return "font-vujahday text-2xl";
    }
  }, [signatureField?.signature?.style]);

  const RenderedSignature = () => {
    if (signatureField?.signature?.dataUrl) {
      return (
        <img
          src={signatureField?.signature?.dataUrl}
          alt="signature"
          className="h-full max-h-24 py-2"
        />
      );
    }

    return (
      <p className={cn("font-medium", fontClassName)}>
        {signatureField?.signature?.text ?? signatureField?.signature}
      </p>
    );
  };

  const previousSignature = useMemo(() => {
    if (questionAttributes?.requireNewSignature) {
      return null;
    }

    const formValues = getValues();
    // Look through all form values to find previous signature questions
    const previousEntry = Object.entries(formValues).find(([key, value]) => {
      const questionNum = Number.parseInt(key);
      return (
        !Number.isNaN(questionNum) &&
        questionNum < questionId &&
        value?.signature &&
        value?.isSignatureComplete &&
        !allSignatureQuestions.find((q) => q.intakeQuestion.id === questionNum)
          ?.intakeQuestion.questionAttributes.requireNewSignature
      );
    });

    return previousEntry ? previousEntry[1] : null;
  }, [getValues, questionId, signatureField]);

  const handleUseLastSignature = () => {
    setValue(`${field.name}.signature`, previousSignature.signature);
    setValue(`${field.name}.isSignatureComplete`, true);
    setValue(
      `${field.name}.date`,
      format(new Date().toISOString(), "MM/dd/yyyy hh:mm a"),
    );
    setSigned(true);
  };

  const handleSignatureButtonClick = () => {
    if (previousSignature && !signed) {
      handleUseLastSignature();
    } else {
      setSigningSheetOpen(true);
    }
  };

  const isMobile = useMediaQuery("(max-width: 768px)");

  return (
    <div className="flex flex-col w-full">
      <div className="flex flex-col w-full gap-1 border rounded-3xl py-4 pl-6 pr-2 relative mb-10">
        <div
          className="overflow-y-auto max-h-[45vh] pr-4 relative
          [&::-webkit-scrollbar]:w-1.5
          [&::-webkit-scrollbar]:absolute
          [&::-webkit-scrollbar-track]:bg-gray-200
          [&::-webkit-scrollbar-track]:rounded-full
          [&::-webkit-scrollbar-track]:mb-[10vh]
          [&::-webkit-scrollbar-thumb]:bg-superscriptBlue
          [&::-webkit-scrollbar-thumb]:rounded-full
          hover:[&::-webkit-scrollbar-thumb]:bg-[#0083cc]"
        >
          <p
            className="text-sm font-medium text-[#000000]"
            data-questionid={questionId}
            data-pdflabel="signatureInput-label"
          >
            {formatRequiredLabel(questionText, isRequired)}
          </p>
          <div
            className="flex flex-col gap-[4px]"
            data-questionid={questionId}
            data-pdflabel="signatureInput-blurb"
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(questionAttributes.blurb),
            }}
          />
        </div>
      </div>

      <div className="flex w-full flex-col gap-8">
        <div className="flex sm:flex-col print:flex-row print:gap-4 lg:gap-4 sm:gap-8 lg:w-full">
          <div className="flex h-fit relative print:basis-3/5 lg:basis-3/5 sm:max-w-full lg:w-full">
            <div className="w-full flex flex-col items-end gap-1">
              <Button
                className={buttonClassName}
                onClick={handleSignatureButtonClick}
              >
                {signed ? (
                  <div className="w-full flex flex-row gap-2 justify-between items-center md:px-6 lg:w-full">
                    <p className="font-medium">{t_signature("Signed")}</p>
                    <RenderedSignature />
                  </div>
                ) : previousSignature ? (
                  <>
                    <p className="font-medium">
                      {t_signature("UseLastSignature")}
                    </p>
                    <RotateCcw size={18} strokeWidth={1} />
                  </>
                ) : (
                  <>
                    <p className="font-medium">{t_signature("SignIfAgree")}</p>
                    <Signature size={18} strokeWidth={1} />
                  </>
                )}
              </Button>
              {signed && (
                <p className="text-sm font-light pr-2">
                  {t_signature("SignedOn", { date: signatureField?.date })}
                </p>
              )}
            </div>

            {signingSheetOpen &&
              (isMobile ? (
                <Sheet
                  open={signingSheetOpen}
                  onOpenChange={setSigningSheetOpen}
                >
                  <SheetPortal>
                    <SheetOverlay
                      onClick={(e) => e.stopPropagation()}
                      className="fixed inset-0 z-50 bg-black/30 backdrop-blur-[1px]"
                    />
                    <SheetContent
                      side="bottom"
                      className="shadow-pentagram rounded-t-3xl h-3/4 z-50"
                    >
                      <TypeformIntakeSignatureSheet
                        field={field}
                        register={register}
                        currentSignature={signatureField}
                        setValue={setValue}
                        getValues={getValues}
                        trigger={trigger}
                        questionId={questionId}
                        updateProgress={updateProgress}
                        isAdmin={isAdmin}
                        initiateAutosave={initiateAutosave}
                        disabled={disabled}
                        setSigningSheetOpen={setSigningSheetOpen}
                        sigCanvas={sigCanvas as RefObject<SignatureCanvas>}
                        setSigned={setSigned}
                      />
                    </SheetContent>
                  </SheetPortal>
                </Sheet>
              ) : (
                <Dialog
                  open={signingSheetOpen}
                  onOpenChange={setSigningSheetOpen}
                >
                  <CustomDialogContent
                    className="max-w-2xl rounded-3xl z-20"
                    preventCloseFromOutsideClick
                  >
                    <TypeformIntakeSignatureSheet
                      field={field}
                      register={register}
                      currentSignature={signatureField}
                      setValue={setValue}
                      getValues={getValues}
                      trigger={trigger}
                      questionId={questionId}
                      updateProgress={updateProgress}
                      isAdmin={isAdmin}
                      initiateAutosave={initiateAutosave}
                      disabled={disabled}
                      setSigningSheetOpen={setSigningSheetOpen}
                      sigCanvas={sigCanvas as RefObject<SignatureCanvas>}
                      setSigned={setSigned}
                    />
                  </CustomDialogContent>
                </Dialog>
              ))}
            <p className="text-[#a92523] absolute font-light text-[10px] bottom-[-21px]">
              {(errors[questionId.toString()] as any)?.isSignatureComplete &&
                t(
                  (errors[questionId.toString()] as any)?.isSignatureComplete
                    ?.message as string,
                )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export const AsyncSignatureDisclosure = () => {
  const userId = useParams<{ userId: string }>().userId;
  const t_intakeForm = useTranslations("IntakeForm");

  const { data: orgId, isLoading: isOrgIdLoading } = useQuery({
    queryKey: ["getUserById", userId],
    queryFn: () => getUserById(userId),
    select: (data) => data?.orgId,
  });

  const { data, isLoading: isOrgLoading } = useQuery({
    queryFn: () => getOrgById(orgId as number),
    queryKey: ["getOrgById", orgId],
    enabled: !!orgId,
  });

  if (isOrgIdLoading || isOrgLoading) {
    return (
      <p className="text-sm text-gray-500">
        {t_intakeForm("SignatureModalBlurb", { orgName: "..." })}
      </p>
    );
  }

  return (
    <p className="text-sm text-gray-500">
      {t_intakeForm("SignatureModalBlurb", { orgName: data?.orgName ?? "..." })}
    </p>
  );
};

// Presentational check indicator for the signature-style selector. Must NOT be
// an interactive control: it renders inside a <Button>, and a real Checkbox
// (Radix renders a <button role="checkbox">) produces invalid <button>-in-
// <button> HTML — a hydration mismatch that makes React regenerate the subtree
// on the client. The parent Button already owns the click; this is display-only.
const SignatureStyleCheck = ({ checked }: { checked: boolean }) => (
  <span
    aria-hidden="true"
    className={cn(
      "flex h-5 w-5 shrink-0 items-center justify-center rounded-sm border border-superscriptBlue",
      checked && "bg-white text-superscriptBlue",
    )}
  >
    {checked && <Check className="h-5 w-5 text-inherit" />}
  </span>
);

export const TypeformIntakeSignatureSheet = ({
  field,
  register,
  currentSignature,
  setValue,
  getValues,
  trigger,
  questionId,
  updateProgress,
  isAdmin,
  initiateAutosave,
  disabled,
  setSigningSheetOpen,
  sigCanvas,
  setSigned,
}: {
  field: ControllerRenderProps<Partial<any>, string>;
  register: any;
  currentSignature: any;
  setValue: UseFormSetValue<Partial<any>>;
  trigger: UseFormTrigger<Partial<any>>;
  getValues: UseFormGetValues<Partial<any>>;
  questionId: number;
  updateProgress: (updatedField: string, updatedValue: any) => void;
  isAdmin: boolean;
  initiateAutosave: (data: Partial<any>, hiddenQuestions?: Set<number>) => void;
  disabled?: boolean;
  setSigningSheetOpen: (open: boolean) => void;
  sigCanvas: RefObject<SignatureCanvas>;
  setSigned: (signed: boolean) => void;
}) => {
  const [signMode, setSignMode] = useState<"drawn" | "typed">("drawn");
  const [signatureStyle, setSignatureStyle] = useState<
    "print" | "classic" | "fancy"
  >("classic");
  const [signatureFilled, setSignatureFilled] = useState(false);
  const t_signature = useTranslations("TypeformIntake");
  const t_intakeForm = useTranslations("IntakeForm");
  const toggleButtonClassName =
    "rounded-2xl font-base text-sm bg-[#F4F4F4] text-black w-full p-6 flex items-center justify-between gap-2";
  const selectedButtonClassName = "border border-black bg-white";

  const fontClassName = useMemo(() => {
    switch (signatureStyle) {
      case "fancy":
        return "font-delafield";
      case "print":
        return "font-vujahday";
      case "classic":
        return "font-madi";
      default:
        return "";
    }
  }, [signatureStyle]);

  const { consumerFullName } = useIntake();

  const [typedSignature, setTypedSignature] = useState(consumerFullName ?? "");

  useEffect(() => {
    if (currentSignature?.signature.dataUrl) {
      if (signMode === "typed") {
        setSignMode("drawn");
      }
      setSignatureFilled(true);
      sigCanvas.current?.fromDataURL(currentSignature?.signature.dataUrl);
    } else if (currentSignature?.signature.text) {
      if (signMode === "drawn") {
        setSignMode("typed");
      }
      setTypedSignature(
        currentSignature?.signature.text ?? currentSignature?.signature ?? "",
      );
      setSignatureStyle(currentSignature?.signature.style ?? "classic");
    }
  }, [currentSignature]);

  useEffect(() => {
    if (signMode === "drawn" && currentSignature?.signature.dataUrl) {
      if (currentSignature?.signature.dataUrl) {
        setSignatureFilled(true);
        sigCanvas.current?.fromDataURL(currentSignature?.signature.dataUrl);
      } else {
        setSignatureFilled(false);
      }
    }
  }, [signMode, currentSignature?.signature.dataUrl]);

  const handleCancel = () => {
    setSigningSheetOpen(false);
  };

  const handleSave = () => {
    if (signMode === "drawn") {
      setValue(
        `${field.name}.signature.dataUrl`,
        sigCanvas.current?.toDataURL(),
      );
    } else {
      setValue(`${field.name}.signature.text`, typedSignature);
      setValue(`${field.name}.signature.style`, signatureStyle);
      setValue(`${field.name}.signature.dataUrl`, null);
    }
    setValue(`${field.name}.isSignatureComplete`, true);
    setValue(
      `${field.name}.date`,
      format(new Date().toISOString(), "MM/dd/yyyy hh:mm a"),
    );
    setSigned(true);
    setSigningSheetOpen(false);
  };

  return (
    <>
      {signMode === "drawn" ? (
        <div className="flex justify-between items-center my-4">
          <div className="flex flex-row gap-2 items-center">
            <h4>{t_signature("SignYourName")}</h4>
            <Signature
              className="min-w-6 min-h-6"
              size={18}
              strokeWidth={1.5}
            />
          </div>
          <X className="w-6 h-6" onClick={handleCancel} />
        </div>
      ) : (
        <div className="flex justify-between items-center my-4 w-full justify-between">
          <ArrowLeft
            className="w-6 h-6"
            onClick={() => {
              setSignMode("drawn");
              if (currentSignature?.signature.dataUrl) {
                setSignatureFilled(true);
              } else {
                setSignatureFilled(false);
              }
            }}
          />
          <div className="flex flex-row gap-2 items-center">
            <h4>{t_signature("TypeName")}</h4>
            <Signature
              className="min-w-6 min-h-6"
              size={18}
              strokeWidth={1.5}
            />
          </div>
          <X className="w-6 h-6" onClick={handleCancel} />
        </div>
      )}

      <div className="flex flex-col gap-4 w-full">
        {signMode === "drawn" ? (
          <>
            <div className="border rounded-2xl overflow-hidden w-full h-[200px] p-2">
              <SignatureCanvas
                ref={sigCanvas}
                canvasProps={{ className: "w-full h-full" }}
                onEnd={() => setSignatureFilled(true)}
              />
            </div>
            {signatureFilled && (
              <Button
                className="px-4 py-2 h-8 flex w-min items-center justify-center bg-white rounded-full border border-black gap-2 text-black font-medium"
                onClick={() => {
                  sigCanvas.current?.clear();
                  setSignatureFilled(false);
                }}
              >
                <RotateCcw className="w-4 h-4" />
                {t_signature("Clear")}
              </Button>
            )}
            <Button
              className="px-4 py-6 flex items-center justify-center bg-white rounded-full border border-black mt-4 gap-2 text-black font-medium md:h-16"
              onClick={() => {
                setSignMode("typed");
                setSignatureFilled(true);
              }}
            >
              {t_signature("TypeNameInstead")}
            </Button>
          </>
        ) : (
          <div className="flex flex-col gap-4 w-full">
            <div className="flex flex-col mb-4">
              <label htmlFor="signature-name" className="text-sm">
                {t_signature("FullName")}
              </label>
              <input
                id="signature-name"
                type="text"
                className={cn(
                  "w-full border rounded-full p-4 font-medium focus:border-black focus:outline-none transition-all duration-300",
                )}
                value={typedSignature}
                onChange={(e) => {
                  setTypedSignature(e.target.value);
                  setSignatureFilled(e.target.value.length > 0);
                }}
              />
            </div>

            <p
              id="signature-preview"
              className={cn("text-4xl min-h-12 w-full", fontClassName)}
            >
              {typedSignature}
            </p>

            <div className="flex flex-col gap-2 w-full">
              <p className="text-md">{t_signature("ChooseStyle")}</p>
              <div className="flex flex-col gap-4">
                <Button
                  className={cn(
                    toggleButtonClassName,
                    signatureStyle === "print" && selectedButtonClassName,
                  )}
                  onClick={() => setSignatureStyle("print")}
                >
                  Print
                  <SignatureStyleCheck checked={signatureStyle === "print"} />
                </Button>
                <Button
                  className={cn(
                    toggleButtonClassName,
                    signatureStyle === "classic" && selectedButtonClassName,
                  )}
                  onClick={() => setSignatureStyle("classic")}
                >
                  Classic
                  <SignatureStyleCheck checked={signatureStyle === "classic"} />
                </Button>
                <Button
                  className={cn(
                    toggleButtonClassName,
                    signatureStyle === "fancy" && selectedButtonClassName,
                  )}
                  onClick={() => setSignatureStyle("fancy")}
                >
                  Fancy
                  <SignatureStyleCheck checked={signatureStyle === "fancy"} />
                </Button>
                <div className="pt-4">
                  <AsyncSignatureDisclosure />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-row gap-4 bottom-0 absolute left-0 w-full p-4 md:static md:justify-between md:p-0 md:mt-12 lg:max-w-2xl lg:px-0 lg:mx-auto lg:mt-28">
        <Button
          className="flex-1 rounded-full bg-white text-black border px-4 py-8 border-black hover:opacity-80 md:h-16 md:w-[266px] md:p-0 md:flex-initial"
          onClick={handleCancel}
        >
          <p className="font-medium text-md ">{t_signature("Cancel")}</p>
        </Button>
        <Button
          className="flex-[2] rounded-full bg-superscriptBlue text-white px-4 py-8 hover:opacity-80 md:h-16 md:w-[266px] md:p-0 md:flex-initial"
          onClick={handleSave}
          disabled={!signatureFilled}
        >
          <p className="font-medium text-md ">{t_signature("Sign")}</p>
        </Button>
      </div>
    </>
  );
};
